import type { TowerConfig, SpecialEffect, Element } from "./types.ts";
import type { Enemy } from "./Enemy.ts";
import { GOLD_TIER } from "../core/config.ts";

/** Default upgrade multipliers — externalized for easy balancing. A tower
 *  may override any subset via TowerConfig.upgradeProfile (F-12). */
export const UPGRADE_MULTIPLIERS = {
  damage: 1.2,
  range: 1.05,
  fireRate: 1.1,
} as const;

export class Tower {
  col = 0;
  row = 0;
  x = 0;
  y = 0;
  element: Element = "fire";
  damage = 0;
  range = 0;
  fireRate = 0;
  projectileSpeed = 0;
  color = "#fff";
  colorLight = "#fff";
  cost = 0;
  cooldown = 0;
  cooldownTimer = 0;
  target: Enemy | null = null;
  special: SpecialEffect | null = null;
  level = 1;
  /** BL-175 / D-266 (S170) — the ceiling is now L6: the GOLD pinnacle tier. L5 is
   *  the top of the L1→L5 arc; the 6th step is GOLD (a real commitment, priced
   *  steeply in `getUpgradeCost`, unlocking the element's signature mechanic).
   *  `isGold()` is the ONE predicate every GOLD combat/render gate reads. */
  maxLevel = GOLD_TIER.LEVEL;
  totalInvested = 0;
  detectStealth = false;
  private upgradeMult: { damage: number; range: number; fireRate: number } = {
    ...UPGRADE_MULTIPLIERS,
  };

  // Beam state (for lightning/EMP)
  beamTarget: Enemy | null = null;
  beamTargetVersion = 0;
  beamDPS = 0;
  beamActive = false;
  beamDamageAccum = 0;
  /** BL-174 / D-263 — lightning's "Overload" surge clock (ms). Banks on-target
   *  beam time; each `special.surgePeriodMs` it forces a GUARANTEED crit, a
   *  dependable cadence on top of the RNG crit (§H3). Reset to 0 on lock-loss
   *  and at init; only ticked at the unlock level for a `surgePeriodMs` special,
   *  so it is inert (0 at every read) for every sub-gate / non-lightning tower. */
  beamSurgeTimer = 0;

  /** BL-175 / D-266 (S170) — the GOLD cadence clock (ms). Banks dt while the tower
   *  is GOLD and fires its signature on a period: water's "ABSOLUTE ZERO" Cryo-Nova
   *  (every `goldNovaPeriodMs`) and lightning's "TEMPEST" Thunderstrike (every
   *  `goldStrikePeriodMs`). Reset to 0 at init and only ticked inside the
   *  `tower.isGold()` gate, so it is inert (0 at every read) for every sub-GOLD /
   *  non-water / non-lightning tower → byte-identical for every fixture. */
  goldTimer = 0;

  /** Render-only (BL-175): the most recent GOLD-signature pulse stamp (ms), set by
   *  the combat layer when a nova/strike fires so the renderer can flash the apex
   *  VFX. Determinism-cold — never read by the sim, decayed in `updateCooldown`. */
  goldPulseT = 0;

  /** BL-175 / D-266 (S170) — signal's GOLD "ORACLE · Command Aura" stamp. RECOMPUTED
   *  every frame in CombatSystem.updateTowers (reset to 1, then multiplied by every
   *  GOLD signal tower in range) BEFORE the behaviors fire, so a buffed tower's
   *  shots this frame already carry it. Read at every damage chokepoint. 1 ⇒ no
   *  buff (the byte-identical default: with no GOLD signal tower, every tower keeps
   *  1, so it has no surface in any fixture). NEVER persisted. */
  commandAuraMult = 1;

  /** Render-only (BL-175): true while this tower is lit by a GOLD ORACLE aura, so
   *  the renderer can draw the empowerment tell. Determinism-cold. */
  commandAuraLit = false;

  // Visual: fire recoil (CR-6)
  fireCooldownVisual = 0;

  /** BL-152 (S154) — the Jammer suppression stamp (ms). A living Act-II Jammer
   *  within range re-stamps this to JAM.WINDOW_MS each tick; CombatSystem decays
   *  it (raw dt) and, while > 0, ticks this tower on "slow-time" (reduced dt →
   *  throttled fire-rate/DPS). 0 ⇒ not jammed (the byte-identical default — every
   *  tower before any Jammer exists, i.e. all of Act I, reads exactly 0). */
  jammedTimer = 0;
  /** S156 / PT-41 — the per-frame dt scale applied while jammed, STAMPED by the
   *  Jammer alongside `jammedTimer` (its `special.fireRateMult`, the lifted-to-
   *  data JAM strength). Read by `CombatSystem.updateTowers` only when
   *  `jammedTimer > 0`, so the default (1 ⇒ no throttle) is inert for every
   *  un-jammed tower → byte-identical. */
  jammedFireRateMult = 1;

  // Sniper state (kinetic tower)
  sniperState: "idle" | "aiming" | "charging" | "recover" = "idle";
  sniperStateTimer = 0;
  /** Charge duration captured on entry to "charging" (ms). Render-only: lets
   *  drawKineticNode derive a correct 0→1 charge progress even when the
   *  designated synergy halves the charge time. */
  sniperChargeDuration = 0;
  sniperTarget: Enemy | null = null;
  sniperTargetVersion = 0;
  sniperSnapShot = false;

  // Visual: kinetic rail tracer — set on fire, decays in updateCooldown so
  // the renderer can draw a fading beam from tower to the shot endpoint.
  railShotTimer = 0;
  railShotX = 0;
  railShotY = 0;
  /** Q-025: smoothed barrel aim (radians). Eased in sim update, read in render. */
  barrelAngle = 0;

  init(
    col: number,
    row: number,
    x: number,
    y: number,
    config: TowerConfig,
  ): this {
    this.col = col;
    this.row = row;
    this.x = x;
    this.y = y;
    this.element = config.element;
    this.damage = config.damage;
    this.range = config.range;
    this.fireRate = config.fireRate;
    this.projectileSpeed = config.projectileSpeed;
    // Colour is injected from the central palette (core/palette.ts) by the
    // DataLoader; fall back to the default if a raw config omits it.
    this.color = config.color ?? this.color;
    this.colorLight = config.colorLight ?? this.colorLight;
    this.cost = config.cost;
    this.cooldown = 1000 / config.fireRate;
    this.cooldownTimer = 0;
    this.target = null;
    this.special = config.special ?? null;
    this.detectStealth = config.detectStealth ?? false;
    this.upgradeMult = { ...UPGRADE_MULTIPLIERS, ...config.upgradeProfile };
    this.level = 1;
    this.totalInvested = config.cost;
    this.beamTarget = null;
    this.beamTargetVersion = 0;
    this.beamDPS = 0;
    this.beamActive = false;
    this.beamDamageAccum = 0;
    this.beamSurgeTimer = 0;
    this.goldTimer = 0;
    this.goldPulseT = 0;
    this.sniperState = "idle";
    this.sniperStateTimer = 0;
    this.sniperChargeDuration = 0;
    this.sniperTarget = null;
    this.sniperTargetVersion = 0;
    this.sniperSnapShot = false;
    this.railShotTimer = 0;
    this.railShotX = 0;
    this.railShotY = 0;
    this.barrelAngle = 0;
    this.jammedTimer = 0;
    this.jammedFireRateMult = 1;
    return this;
  }

  /** True once this tower has been upgraded to the GOLD pinnacle (L6). The ONE
   *  predicate every GOLD combat path + the GOLD render layer + the "GOLD" label
   *  gate on, so the signature mechanic has a single source of truth and stays
   *  wholly inert below GOLD (no fixture ever reaches L6 → byte-identical). */
  isGold(): boolean {
    return this.level >= GOLD_TIER.LEVEL;
  }

  getUpgradeCost(): number {
    if (this.level >= this.maxLevel) return Infinity;
    // BL-175 / D-266 — the GOLD step (L5→L6) is the pinnacle commitment: it costs
    // a multiple of the tower's TOTAL prior investment (cost + every L1→L5
    // upgrade), i.e. ~the whole investment again, ~5× a normal late upgrade. This
    // drains the 1500→3500 surplus buffer and self-scales per tower, so the steep
    // towers stay scarce with NO hard cap (the price alone is the limiter).
    if (this.level === this.maxLevel - 1) {
      return Math.round(this.totalInvested * GOLD_TIER.COST_INVESTMENT_MULTIPLE);
    }
    return Math.round(this.cost * (0.5 + this.level * 0.5));
  }

  /** Returns the upgrade cost for display purposes, or null when the tower is
   *  at max level. Single source of truth for the "maxed ⇒ null" decision;
   *  both TowerSelector.showTowerInfo and renderPipeline call this so the
   *  conditional lives in exactly one place (PT-16 / D-135). */
  getDisplayUpgradeCost(): number | null {
    return this.level >= this.maxLevel ? null : this.getUpgradeCost();
  }

  /** The EXACT stats this tower will have after the next `upgrade()` — computed
   *  with this tower's REAL per-element multipliers (`upgradeProfile`-aware) and
   *  the SAME rounding as `upgrade()`, so the tooltip preview can never promise a
   *  gain the runtime won't deliver. Returns `null` at max level. The single
   *  source of truth behind the paper↔tooltip↔runtime net (D-262): the upgrade
   *  preview previously hard-coded the DEFAULT multipliers, which LIED for the
   *  two `upgradeProfile` towers (kinetic range×1/rate×1, signal range×1.1/
   *  rate×1.15). */
  previewUpgrade(): {
    damage: number;
    range: number;
    fireRate: number;
  } | null {
    if (this.level >= this.maxLevel) return null;
    return {
      damage: Math.round(this.damage * this.upgradeMult.damage),
      range: Math.round(this.range * this.upgradeMult.range),
      fireRate:
        Math.round(this.fireRate * this.upgradeMult.fireRate * 100) / 100,
    };
  }

  upgrade(): boolean {
    if (this.level >= this.maxLevel) return false;
    const cost = this.getUpgradeCost();
    this.totalInvested += cost;
    this.level++;
    this.damage = Math.round(this.damage * this.upgradeMult.damage);
    this.range = Math.round(this.range * this.upgradeMult.range);
    this.fireRate =
      Math.round(this.fireRate * this.upgradeMult.fireRate * 100) / 100;
    this.cooldown = 1000 / this.fireRate;
    return true;
  }

  downgrade(): boolean {
    if (this.level <= 1) return false;
    this.level--;
    this.damage = Math.round(this.damage / this.upgradeMult.damage);
    this.range = Math.round(this.range / this.upgradeMult.range);
    this.fireRate =
      Math.round((this.fireRate / this.upgradeMult.fireRate) * 100) / 100;
    this.cooldown = 1000 / this.fireRate;
    return true;
  }

  updateCooldown(dt: number): void {
    if (this.cooldownTimer > 0) {
      this.cooldownTimer -= dt;
    }
    if (this.fireCooldownVisual > 0) {
      this.fireCooldownVisual = Math.max(0, this.fireCooldownVisual - dt);
    }
    if (this.railShotTimer > 0) {
      this.railShotTimer = Math.max(0, this.railShotTimer - dt);
    }
    // BL-175 — decay the GOLD-pulse render stamp (determinism-cold; render-only).
    if (this.goldPulseT > 0) {
      this.goldPulseT = Math.max(0, this.goldPulseT - dt);
    }
  }

  canFire(): boolean {
    return this.cooldownTimer <= 0;
  }

  fire(): void {
    this.cooldownTimer = this.cooldown;
    this.fireCooldownVisual = 80; // 80ms decay for recoil animation
  }
}
